/**
 * The federal console's one client. TDD 30 Sections 7.1 to 7.3.
 *
 * Three properties are the whole design, and each exists because of something in this
 * repository rather than as a preference.
 *
 * 1. A MISSING ENDPOINT IS A STATE, NOT A CRASH. Several federal endpoints are being
 *    built in parallel with this console. A client that threw on a 404 would take the
 *    whole page down whenever a sibling stream had not landed yet, so a 404 on a
 *    federal path resolves to `status: 'unavailable'` carrying the path, and the region
 *    renders a named empty state instead. `isServed` can also ask the running build
 *    which federal paths it publishes, so a control that cannot commit is disabled with
 *    that as its reason rather than failing when an operator presses it.
 *
 * 2. A 401 DOES NOT NAVIGATE. `apiClientFetch` in `src/lib/api-client.ts` answers a 401
 *    by calling `signIn('keycloak')`, which leaves the page. That is right for the
 *    existing console and wrong here: every federal path except `/scc/status` sits
 *    behind `verify_access`, and an operations console that vanishes mid-read cannot
 *    tell anyone what it was missing. A 401 resolves to `status: 'unauthenticated'`.
 *
 * 3. NO DATA-FETCHING LIBRARY. `30-R041` and the reason given with it: the existing
 *    console has none, adding one changes the bundle every client ships. This module is
 *    a fetch, a small hook and a path table.
 *
 * The module writes nothing. Every mutation this console will need is declared here as
 * a path so that the surface is countable, and the two implemented POST paths are
 * exposed as functions; the console shell itself only reads.
 */
'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { getSession } from 'next-auth/react'
import { API_URL } from '@/lib/api-client'
import type { Resource, ResourceError } from '@/lib/scc-types'

/** Mirrors the expression in `src/lib/api-client.ts`, which does not export it. */
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

/**
 * The lifecycle whose stage rail the console renders.
 *
 * A DIVERGENCE from `30-R078`, which requires that no lifecycle code literal appear in
 * the federal tree and that the code always arrive from the backend. No endpoint lists
 * the lifecycles: `GET /scc/lifecycles/{code}` reads one by code and there is no index.
 * Until one exists the console cannot render a rail without naming a code, so the code
 * is named exactly once, here, overridable by configuration, and it is the same code
 * `scripts/scc_walkthrough.py` drives the bring-up check against. When a lifecycle
 * index lands this constant is deleted and the frame takes the code from its envelope.
 */
export const FEDERAL_LIFECYCLE_CODE =
  process.env.NEXT_PUBLIC_SCC_LIFECYCLE_CODE || 'federal_generic_18'

/**
 * Every federal path the console knows, in one table.
 *
 * Templates carry `{}` placeholders in the form the OpenAPI schema uses, so
 * `tests/federal/test_console_shell.py` can compare this table against the routes the
 * application actually serves and fail when the two drift apart. A path the console
 * calls that the backend does not serve is then a test failure at build time rather
 * than an empty region a reader has to explain.
 */
export const FEDERAL_PATHS = {
  /** Served, and public. */
  status: '/api/scc/status',
  /** Served. */
  lifecycle: '/api/scc/lifecycles/{code}',
  authorities: '/api/scc/authorities',
  exceptions: '/api/scc/acquisitions/{acquisition_id}/exceptions',
  exceptionSummary: '/api/scc/acquisitions/{acquisition_id}/exceptions/summary',
  exceptionResolve: '/api/scc/exceptions/{exception_id}/resolve',
  exceptionTypes: '/api/scc/exception-types',
  gate: '/api/scc/acquisitions/{acquisition_id}/gates/{gate_code}',
  gateSlotDecision:
    '/api/scc/acquisitions/{acquisition_id}/gates/{gate_code}/slots/{slot_id}',
  acquisitionIndex: '/api/scc/acquisitions',
  acquisitionRecord: '/api/scc/acquisitions/{acquisition_id}',
  inboundMessages: '/api/scc/inbound/messages',
  inboundInjection: '/api/scc/inbound/messages',
  inboundArrivalChannels: '/api/scc/inbound/arrival-channels',
  /**
   * The Agent Hub, Auto Insights and Auto Policies paths.
   *
   * WHY THEY ARE HERE AND NOT ONLY IN THEIR COMPONENTS: this table is what
   * `tests/federal/test_console_shell.py:79` asserts against the routes the application
   * serves, and until now it listed thirteen paths and no screen path among them. Nine
   * components hard-coded their own literals, and one of them —
   * `OrchestrationPlan.tsx` — hard-coded a plan code of `'reference'` against a plan
   * seeded as `federal_reference_18`, so a 404 reached the console on every load of the
   * Orchestration tab and nothing at build time could see it. A path declared here is a
   * path a test can check; a literal in a component is not.
   */
  /**
   * The whole Agent Hub — three tabs, one request. `11_AGENT_HUB.md` §Backend.
   *
   * The screen reads this and nothing else. The per-operator performance path below
   * stays served and stays tested; it is simply no longer on the hub's critical path,
   * which is what turned twenty-two requests per page load into one.
   */
  agentHub: '/api/scc/agent-hub/hub',
  agentHubOrchestrators: '/api/scc/agent-hub/orchestrators',
  agentHubOperators: '/api/scc/agent-hub/operators',
  agentHubOperatorPerformance: '/api/scc/agent-hub/operators/{code}/performance',
  agentHubActivity: '/api/scc/agent-hub/activity',
  agentHubRuns: '/api/scc/agent-hub/runs',
  agentHubRun: '/api/scc/agent-hub/runs/{run_id}',
  agentHubRunEvents: '/api/scc/agent-hub/runs/{run_id}/events',
  agentHubPlan: '/api/scc/agent-hub/plans/{code}',
  insightsOperational: '/api/scc/insights/operational',
  insightsAssurance: '/api/scc/insights/assurance',
  insightsExceptions: '/api/scc/insights/exceptions',
  insightsMeasures: '/api/scc/insights/measures',
  insightsMeasureRecords: '/api/scc/insights/measures/{code}/records',
  policies: '/api/scc/policies',
  policy: '/api/scc/policies/{code}',
  submissionFindings: '/api/scc/submissions/{submission_id}/findings',
  findingDecision: '/api/scc/findings/{finding_id}/decision',
  /**
   * The resolution write, which returns both version numbers per 20-R017.
   * `exceptionResolve` above is the one-row `/resolve` route; this is the one the
   * console commits through.
   */
  exceptionResolution: '/api/scc/exceptions/{exception_id}/resolution',
  /**
   * The landing page's whole payload in one read, and the operator feed on its own so
   * that panel can refresh without re-running eight aggregations.
   *
   * One request for one screen is deliberate. The federal dashboard it replaced issued
   * three — `/scc/status`, `/scc/insights/operational` and `/scc/insights/exceptions` —
   * and rendered three independent loading states that resolved at three different
   * moments, so the page assembled itself in front of the reader.
   */
  dashboard: '/api/scc/dashboard',
  dashboardActivity: '/api/scc/dashboard/activity',
} as const

/**
 * The paths the console reads that this build is not expected to serve yet.
 *
 * Declared rather than discovered so that the gap is countable: a reviewer can read
 * this list and know exactly which regions of the console are showing a named empty
 * state because the route does not exist. Removing an entry is how the console records
 * that a stream landed.
 *
 * Empty, and that is the current fact rather than an unfinished declaration. This
 * stream was written while the acquisition index and the inbound routes were unmounted,
 * and listed both. Both are served, and every path in the table above answers when it is
 * requested — which `tests/federal/test_console_shell.py` establishes by requesting each
 * one against a control, rather than by reading the generated schema or this comment.
 * The consequence is the visible one: those regions render the record instead of
 * explaining why they cannot.
 */
export const NOT_YET_SERVED: readonly string[] = []

/**
 * Fill a path template, for example
 * `fill(FEDERAL_PATHS.gate, { acquisition_id: 3, gate_code: someGate.code })`.
 *
 * The gate code always comes from a response. No gate code is written as a literal
 * anywhere in this tree, per `30-R015`, and not in this comment either.
 */
export function fill(
  template: string,
  values: Record<string, string | number>
): string {
  return template.replace(/\{(\w+)\}/g, (_match, key: string) => {
    const value = values[key]
    if (value === undefined) {
      // Loud rather than silent: a template rendered with a hole in it would request a
      // path containing a brace and read back as a 404, which reports the wrong cause.
      throw new Error(`no value for {${key}} in ${template}`)
    }
    return encodeURIComponent(String(value))
  })
}

/** Carries the status and the path so a region can name what is missing. */
export class FederalRequestError extends Error {
  readonly status: number
  readonly path: string
  readonly detail: unknown

  constructor(message: string, status: number, path: string, detail: unknown) {
    super(message)
    this.name = 'FederalRequestError'
    this.status = status
    this.path = path
    this.detail = detail
  }
}

/**
 * The server's own sentence out of a refusal body. 30-R098.
 *
 * The federal envelope is `{"error": {"code", "message", "detail": {...}}}` (20-R011).
 * `federalGet` below extracts `body.detail ?? body`, so for a body whose only top-level
 * key is `error` this receives the whole `{error: {...}}` object and the message is one
 * level further down. Reading only the top level made every refusal from
 * `app/routers/scc/stage_phase2.py` and `app/routers/scc/submissions.py` — most of
 * Phase 2 and every Stage-8/9 interaction on the demo path — arrive as the bare word
 * `Not Found`, discarding the sentence the server had written for exactly that moment.
 *
 * The nested read is tried first because a body carrying `error.message` always means
 * it, whereas a top-level `detail` can be an object.
 */
function messageFrom(detail: unknown, fallback: string): string {
  if (typeof detail === 'string' && detail.trim()) return detail
  if (detail && typeof detail === 'object') {
    const record = detail as Record<string, unknown>
    const nested = record.error
    if (nested && typeof nested === 'object') {
      const nestedMessage = (nested as Record<string, unknown>).message
      if (typeof nestedMessage === 'string' && nestedMessage.trim()) return nestedMessage
    }
    const message = record.message ?? record.detail
    if (typeof message === 'string' && message.trim()) return message
  }
  return fallback
}

/**
 * One GET against a federal path.
 *
 * The session token is attached where a session exists and omitted where none does,
 * with no sign-in attempt either way. `/scc/status` is in `app/public.map.json` and so
 * answers without one, which is what lets the console explain itself before anyone has
 * logged in.
 */
export async function federalGet<T>(path: string): Promise<T> {
  const headers = new Headers({ Accept: 'application/json' })
  try {
    const session = await getSession()
    if (session?.accessToken) {
      headers.set('Authorization', `Bearer ${session.accessToken}`)
    }
  } catch {
    // No session provider mounted, or the session endpoint failed. Continue without a
    // token: the request either succeeds because the path is public or resolves to
    // `unauthenticated`, and both are more useful than a thrown session error.
  }

  let response: Response
  try {
    response = await fetch(`${API_URL}${BASE_PATH}${path}`, {
      headers,
      cache: 'no-store',
    })
  } catch (cause) {
    throw new FederalRequestError(
      cause instanceof Error ? cause.message : 'the request did not reach the server',
      0,
      path,
      null
    )
  }

  if (!response.ok) {
    const detail = await response
      .json()
      .then((body: { detail?: unknown }) => body.detail ?? body)
      .catch(() => response.statusText)
    throw new FederalRequestError(
      messageFrom(detail, response.statusText || `HTTP ${response.status}`),
      response.status,
      path,
      detail
    )
  }

  return (await response.json()) as T
}

/** One POST against a federal path. Same 401 and 404 handling as the read path. */
export async function federalPost<T>(path: string, body: unknown): Promise<T> {
  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    // The channel every audit entry records for a console action. `acting_context`
    // reads this header and defaults it to the same value; sending it explicitly means
    // the recorded channel is a fact about the caller rather than about a default.
    'X-Channel': 'console',
  })
  try {
    const session = await getSession()
    if (session?.accessToken) {
      headers.set('Authorization', `Bearer ${session.accessToken}`)
    }
  } catch {
    // As in federalGet: proceed and let the status describe what happened.
  }

  let response: Response
  try {
    response = await fetch(`${API_URL}${BASE_PATH}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })
  } catch (cause) {
    throw new FederalRequestError(
      cause instanceof Error ? cause.message : 'the request did not reach the server',
      0,
      path,
      null
    )
  }

  if (!response.ok) {
    const detail = await response
      .json()
      .then((parsed: { detail?: unknown }) => parsed.detail ?? parsed)
      .catch(() => response.statusText)
    throw new FederalRequestError(
      messageFrom(detail, response.statusText || `HTTP ${response.status}`),
      response.status,
      path,
      detail
    )
  }

  if (response.status === 204) return null as T
  return (await response.json()) as T
}

/** Turn a thrown error into the resource state that describes it honestly. */
export function resourceFromError<T>(error: unknown): Resource<T> {
  const failure: ResourceError =
    error instanceof FederalRequestError
      ? { status: error.status, message: error.message, path: error.path }
      : {
          status: 0,
          message: error instanceof Error ? error.message : String(error),
          path: '',
        }

  // 404 is the parallel-build case and 501 is a route that exists but is unimplemented.
  // Both mean "there is no answer to be had here", which is neither an error to retry
  // nor an empty result set.
  const status =
    failure.status === 404 || failure.status === 501
      ? 'unavailable'
      : failure.status === 401 || failure.status === 403
        ? 'unauthenticated'
        : 'error'

  return { status, data: null, error: failure, isRefetching: false }
}

// ---------------------------------------------------------------------------
// The served-path probe
// ---------------------------------------------------------------------------

/**
 * The paths this console asks the build about, one request each.
 *
 * A probe costs a request, so this is the paths whose absence changes what the console
 * *renders* rather than every path it reads. A read that fails answers for itself
 * through `resourceFromError`; a control that would commit a write has to be disabled
 * with a reason before anyone presses it, and that is the case this table is for.
 */
export const PROBED_PATHS: readonly string[] = [FEDERAL_PATHS.inboundInjection]

/**
 * A path no route declares, requested as the control on every probe run.
 *
 * A probe that reads a status is only evidence if a missing route answers differently
 * from a present one. If this path ever answers anything other than 404 — a proxy
 * rewriting unknown paths, an authentication layer refusing before routing — then no
 * status this function reads distinguishes served from absent, and the honest answer
 * is that it does not know.
 */
const PROBE_CONTROL_PATH = '/api/scc/no-route-declares-this-path'

/** Fills a template's placeholders. Routing matches on path shape, not on the value. */
const PROBE_SEGMENT = '0'

let servedPathsPromise: Promise<ReadonlySet<string>> | null = null

/** The status of one OPTIONS request, or 0 where the request did not arrive. */
async function probeStatus(path: string): Promise<number> {
  try {
    const response = await fetch(`${API_URL}${BASE_PATH}${path}`, {
      method: 'OPTIONS',
      cache: 'no-store',
    })
    return response.status
  } catch {
    // Offline, blocked, or refused by CORS. Reported as 0 and read as "unknown"
    // below, never as "absent".
    return 0
  }
}

/**
 * Which of `PROBED_PATHS` this build serves, established by requesting each one.
 *
 * WHY THIS ISSUES REQUESTS AND NO LONGER READS `/api/openapi.json`. The generated
 * schema is a document about the application, not the application. `FastAPI.openapi()`
 * keys `paths` in a dict, so where a path is registered twice the LATER registration
 * wins the schema while the EARLIER one wins request matching:
 * `GET /scc/acquisitions/{id}/exceptions` was declared in two modules, and the schema
 * therefore advertised a bare array for a path that answers `{count, items}`. That is
 * D22 in `40_KNOWN_DEFECTS_AND_FIXES.md`, and it is why nothing reading the schema can
 * see a shadowed route. A request reaches the handler that actually answers.
 *
 * OPTIONS rather than GET: Starlette answers a declared path whose methods do not
 * include OPTIONS with 405 and an `Allow` header, and an undeclared path with 404,
 * without running a handler, touching the database or needing an identity. What that
 * proves is that the PATH is declared, not that a particular method on it is — no
 * side-effect-free request can prove that a POST route exists, and inventing one to
 * find out would be worse than not knowing.
 *
 * Read once per page load and memoised: it is a property of the build, so polling it
 * would be `30-R044`'s defect with extra steps.
 */
export async function servedFederalPaths(): Promise<ReadonlySet<string>> {
  if (!servedPathsPromise) {
    servedPathsPromise = (async () => {
      // Unknown is every path, because `isServed` must degrade to attempting the
      // request rather than to a console that refuses to try.
      const unknown = new Set(PROBED_PATHS)
      if ((await probeStatus(PROBE_CONTROL_PATH)) !== 404) return unknown

      const answered = await Promise.all(
        PROBED_PATHS.map(async template => {
          const status = await probeStatus(template.replace(/\{\w+\}/g, PROBE_SEGMENT))
          // Only a 404 is evidence of absence. 401, 403, 405 and 200 all mean the path
          // is declared, and 0 means the probe never arrived.
          return status === 404 ? null : template
        })
      )
      return new Set(answered.filter((t): t is string => t !== null))
    })()
  }
  return servedPathsPromise
}

/**
 * Whether the build serves a path template.
 *
 * A template nobody probed reads as served, so adding a path to `FEDERAL_PATHS` never
 * silently disables a control: only a probe that answered 404 does.
 */
export function isServed(served: ReadonlySet<string>, template: string): boolean {
  if (!PROBED_PATHS.includes(template)) return true
  return served.has(template)
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

const LOADING: Resource<never> = {
  status: 'loading',
  data: null,
  error: null,
  isRefetching: false,
}

export interface UseFederalResourceOptions<T> {
  /**
   * Whether a successful response holds nothing. Empty and error are never
   * substituted for one another, per `30-R101`: a 200 carrying `[]` is empty, a
   * failed request is an error, and the two say different things to an operator.
   */
  isEmpty?: (data: T) => boolean
  /** Skip the request entirely and hold the given state. Used where a path is unfilled. */
  skip?: boolean
}

export interface FederalResource<T> {
  resource: Resource<T>
  /** Refetch without blanking the region: `isRefetching` carries the reload. */
  reload: () => void
}

/**
 * Read one federal path into one region's state.
 *
 * A reload keeps the previous `data` rendered and sets `isRefetching`, per `30-R045`:
 * a region that blanks on refresh loses the operator's place for no gain.
 */
export function useFederalResource<T>(
  path: string,
  options: UseFederalResourceOptions<T> = {}
): FederalResource<T> {
  const [resource, setResource] = useState<Resource<T>>(LOADING as Resource<T>)
  const [nonce, setNonce] = useState(0)

  // Held in a ref so that passing an inline predicate does not re-run the request on
  // every render. The predicate is a property of the caller, not of the request.
  const isEmptyRef = useRef(options.isEmpty)
  isEmptyRef.current = options.isEmpty
  const skip = options.skip ?? false

  useEffect(() => {
    if (skip) return
    let live = true
    setResource(prior =>
      prior.data === null ? (LOADING as Resource<T>) : { ...prior, isRefetching: true }
    )

    federalGet<T>(path)
      .then(data => {
        if (!live) return
        const empty = isEmptyRef.current ? isEmptyRef.current(data) : false
        setResource({
          status: empty ? 'empty' : 'ready',
          data,
          error: null,
          isRefetching: false,
        })
      })
      .catch(error => {
        if (!live) return
        setResource(resourceFromError<T>(error))
      })

    return () => {
      // The component unmounted or the path changed: a late response must not write
      // over the state belonging to a newer request.
      live = false
    }
  }, [path, nonce, skip])

  const reload = useCallback(() => setNonce(n => n + 1), [])
  return { resource, reload }
}

/** The served-path set as a resource, so a region can wait on it like any other read. */
export function useServedFederalPaths(): ReadonlySet<string> {
  // Served until the probe says otherwise. Starting empty would label every probed
  // control unserved for as long as the probe takes, and a control that explains it
  // cannot commit and then can is worse than one that is simply attempted.
  const [served, setServed] = useState<ReadonlySet<string>>(() => new Set(PROBED_PATHS))
  useEffect(() => {
    let live = true
    servedFederalPaths().then(paths => {
      if (live) setServed(paths)
    })
    return () => {
      live = false
    }
  }, [])
  return served
}

/** True where the list read returned no rows. Shared so every list agrees on empty. */
/**
 * The envelope every federal listing route answers with.
 *
 * WHY THIS TYPE EXISTS, AND IT IS THE MOST EXPENSIVE LESSON IN THIS FILE. Four screens
 * declared a listing response as a bare array, because that is the obvious shape for a
 * list. Every federal listing route answers with an object instead: `{items, count}` for
 * the acquisition index and the inbound queue, and `{as_of, operator_count, operators}`
 * for the operator registry. The count and the acting authority belong on the response
 * once rather than on every row, which is right, and nothing told the console.
 *
 * The consequence was not a wrong number. `rows.filter` on an object throws, so the
 * dashboard rendered "Application error: a client-side exception has occurred" and the
 * page was blank. It survived a build, a typecheck and 883 tests, because a declared type
 * is an assertion about a server and TypeScript checks the assertion's internal
 * consistency rather than its truth. Three of the four had never met a real response: the
 * routes were listed as not yet served, so the screens were only ever exercised against
 * the empty state.
 *
 * Declare a listing response as `Listing<Row>` and read it through `itemsOf`.
 */
export interface Listing<T> {
  items: T[]
  count: number
}

/**
 * The rows of a listing response, whatever shape it arrived in.
 *
 * Tolerates a bare array as well as the envelope, because `/authorities` and
 * `/exception-types` genuinely do answer with one and a caller should not have to know
 * which. Anything else yields no rows rather than throwing: a screen that renders its
 * empty state is recoverable, and one that throws takes the whole page with it.
 */
export function itemsOf<T>(payload: Listing<T> | T[] | null | undefined): T[] {
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray((payload as Listing<T>).items)) {
    return (payload as Listing<T>).items
  }
  return []
}

/**
 * Whether a listing response carries no rows.
 *
 * Reads through the envelope. Left as an array-only test it silently returned false for
 * every envelope, so a screen with nothing to show rendered an empty region instead of the
 * named empty state that says why.
 */
export function isEmptyList(rows: unknown): boolean {
  if (Array.isArray(rows)) return rows.length === 0
  if (rows && typeof rows === 'object' && Array.isArray((rows as { items?: unknown }).items)) {
    return (rows as { items: unknown[] }).items.length === 0
  }
  return false
}
